import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  const categories = ["All", "Pharmaceutical", "Construction", "Manufacturing", "Architecture"];
  
  const filteredProjects = filter === "All" 
    ? projects 
    : projects.filter(p => p.category === filter);

  const industries = [
    { name: "Pharmaceutical", image: "https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/ni6kzttb_clean-room-luminaires-3.jpg" },
    { name: "Construction", image: "https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/8asxqucy_smoking%20booth%20031.jpg" },
    { name: "Architecture", image: "https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/p5ry1xqt_MU-BIM-Image-1024x670.jpg" },
    { name: "Manufacturing", image: "https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/lil9myfp_welding-2022-12-15-19-18-12-utc.jpg" }
  ];

  const capabilities = [
    { title: "Precision Tolerance", value: "±0.1mm", description: "Industry-leading precision in all our fabrication work" },
    { title: "Material Range", value: "0.5-50mm", description: "Capability to work with a wide range of material thicknesses" },
    { title: "Max Sheet Size", value: "3m x 1.5m", description: "Large format sheet handling capabilities" }
  ];

  return (
    <div data-testid="projects-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1739599211500-74e04a9ca175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGVlbCUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwc3RydWN0dXJlfGVufDB8fHx8MTc3MDA2MDIyMnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Portfolio
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Featured <span className="text-blue-500">Projects</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto">
              Explore our portfolio of successful projects across pharmaceutical, construction, 
              manufacturing, and architectural sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`filter-${category.toLowerCase()}`}
                variant={filter === category ? "default" : "outline"}
                className={`rounded-sm uppercase text-xs tracking-wider ${
                  filter === category 
                    ? "bg-blue-600 hover:bg-blue-500 text-white" 
                    : "border-slate-700 hover:border-blue-500 hover:text-blue-400"
                }`}
                onClick={() => setFilter(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden card-hover group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent opacity-80" />
                  <Badge 
                    className="absolute top-4 left-4 bg-blue-600 text-white rounded-sm"
                  >
                    {project.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 
                    className="text-xl font-bold text-white uppercase tracking-tight mb-3 group-hover:text-blue-400 transition-colors"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {project.title}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {project.description}
                  </p>
                  <Link 
                    to={project.link || "/contact"}
                    className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors text-sm uppercase tracking-wider"
                  >
                    {project.link ? "View Project Details" : "Discuss Similar Project"}
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Capabilities
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Technical <span className="text-blue-500">Excellence</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {capabilities.map((capability, idx) => (
              <div 
                key={idx}
                data-testid={`capability-${idx}`}
                className="text-center"
              >
                <p 
                  className="text-5xl font-bold text-blue-500 mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {capability.value}
                </p>
                <h3 className="text-white font-bold uppercase tracking-wider mb-2">
                  {capability.title}
                </h3>
                <p className="text-slate-400 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Industries We Serve
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Sector <span className="text-blue-500">Expertise</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, idx) => (
              <div 
                key={idx}
                data-testid={`industry-${idx}`}
                className="relative group overflow-hidden rounded-sm"
              >
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-slate-950/70 flex items-center justify-center">
                  <h3 
                    className="text-white font-bold uppercase tracking-wider text-center"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {industry.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Have a Project in Mind?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Let's discuss how Nassau Engineering can bring your vision to life with 
            precision fabrication and expert engineering.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="projects-cta-btn"
              size="lg"
              className="bg-white text-blue-600 hover:bg-slate-100 rounded-sm uppercase tracking-wider"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
